import { createTRPCReact, type CreateTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import type { AppRouter } from "../../../api/src/trpc";
import { sessionStore } from "./session-store";

type TRPCClient = ReturnType<CreateTRPCReact<AppRouter, unknown>["createClient"]>;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

/**
 * tRPC React hooks
 */
export const trpc: CreateTRPCReact<AppRouter, unknown> = createTRPCReact<AppRouter>();

/**
 * Single-flight silent token refresh.
 *
 * The access-token cookie lives 15 minutes; the refresh-token cookie lives 7
 * days. When the access token expires, protected procedures return HTTP 401.
 * Rather than log the user out, we transparently POST /auth/refresh (which
 * rotates the tokens and re-sets both httpOnly cookies) and retry the original
 * request once. Concurrent 401s share one in-flight refresh so we never stampede
 * the endpoint.
 */
let refreshPromise: Promise<boolean> | null = null;
function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/** Public auth procedures: their 401 means "bad token/credentials", never "access token expired". */
const PUBLIC_PROCEDURES = new Set([
  "auth.consumeMagicLink",
  "auth.requestMagicLink",
  "auth.checkInvitation",
  "auth.acceptInvitation",
]);

function isPublicOnly(url: string): boolean {
  const names = decodeURIComponent(url.split("/trpc/")[1]?.split("?")[0] ?? "").split(",");
  return names.length > 0 && names.every((n) => PUBLIC_PROCEDURES.has(n));
}

/**
 * tRPC client configuration
 *
 * SECURITY: Uses httpOnly cookies for authentication
 * - Tokens are never exposed to JavaScript (XSS protection)
 * - Cookies are automatically included with credentials: 'include'
 * - CSRF protection via x-trpc-source custom header
 */
export const trpcClient: TRPCClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      async fetch(url, options) {
        const urlStr = url.toString();
        const doFetch = () => fetch(urlStr, { ...options, credentials: "include" });

        const res = await doFetch();

        // Access token expired → all protected procedures in the batch 401.
        // Refresh the session once and retry transparently so the user isn't
        // bounced mid-session. If refresh fails (no/'expired refresh token),
        // the original 401 flows through and the app treats it as logged out.
        if (res.status === 401 && !isPublicOnly(urlStr) && !sessionStore.isExpired()) {
          const refreshed = await refreshSession();
          if (refreshed) {
            // Whatever the retry answers reaches the caller: a 401 here can be a business
            // error (e.g. "Invalid TOTP code" on approve/provision), not a dead session.
            return doFetch();
          }
          // The refresh itself failed: the session is really gone. Open the global
          // Session expired dialog.
          sessionStore.markExpired();
        }
        return res;
      },
      headers() {
        return {
          "x-trpc-source": "web", // CSRF protection header (triggers CORS preflight)
        };
      },
    }),
  ],
});
